import React from 'react';
import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendScrollComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsOption } from 'echarts';
import { formatInTimeZone } from 'date-fns-tz';
import { DEFAULT_DATE_FORMAT, getComputerFormattedTime } from '../../../domain/date/date';
import { isNumber, isArray } from 'lodash';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { renderTooltip } from './chartUtils';
import { PRIMARY_COLOR } from '../../../theme';

echarts.use([BarChart, LineChart, TooltipComponent, GridComponent, CanvasRenderer, LegendScrollComponent]);

export interface IHistoricalPerformanceChartData {
    baselineKw: number | null;
    nominationKw: number | null;
    performanceKw: number | null;
    meters: number;
    eventStart: Date;
    initialEvent: boolean; // Event, we opened the chart from
    eventDuration: number; // minutes
}

interface IHistoricalPerformanceChartProps {
    data: IHistoricalPerformanceChartData[];
    timezone: string;
}

export const HistoricalPerformanceChart = ({ data, timezone }: IHistoricalPerformanceChartProps) => {
    const categories = data.map(item => {
        return `${formatInTimeZone(item.eventStart, timezone, DEFAULT_DATE_FORMAT)} \n ${formatInTimeZone(item.eventStart, timezone, 'hh:mm a')} ${getComputerFormattedTime(new Date(), 'zzz')}`;
    });

    const option: EChartsOption = {
        legend: {
            data: ['Nomination', 'Performance'],
            selectedMode: false,
        },
        grid: {
            top: '50px',
            left: '80px',
            right: '70px',
            bottom: '70px',
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                snap: true,
                lineStyle: {
                    type: 'solid',
                },
            },
            position: (point, params, dom, rect, size) => {
                const [tooltipWidth, tooltipHeight] = size.contentSize;

                return [point[0] - tooltipWidth / 2, point[1] - (tooltipHeight + 20)];
            },
            formatter: (params: TopLevelFormatterParams) => {
                const dataIndex: number = isArray(params) ? params[0].dataIndex : 0;
                return renderTooltip(data[dataIndex], timezone);
            },
        },
        xAxis: {
            type: 'category',
            data: categories,
            splitLine: {
                show: true,
            },
            axisLabel: {
                color: '#697475',
                lineHeight: 16,
            },
            z: 2,
            axisLine: {
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                },
            },
        },
        yAxis: {
            type: 'value',
            max: (value: any) => {
                return Math.round(value.max * 1.3);
            },
            axisLabel: {
                formatter: (value: number) => value.toLocaleString() + ' kW',
                color: '#697475',
                showMaxLabel: false,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                },
            },
            splitLine: { show: false },
        },

        series: [
            {
                name: 'Nomination bar',
                type: 'bar',
                data: data.map(item => item.nominationKw),
                barWidth: 60,
                barGap: '-100%',
                z: 1,
                itemStyle: {
                    color: 'rgba(187, 195, 195, 0.6)',
                },
                markArea: {
                    itemStyle: {
                        color: '#f7f8f8',
                    },
                    silent: true,
                    data: [
                        [
                            {
                                xAxis: data.findIndex(item => item.initialEvent),
                            },
                            {
                                xAxis: data.findIndex(item => item.initialEvent),
                            },
                        ],
                    ],
                },
            },
            {
                name: 'Nomination',
                type: 'line',
                data: data.map(item => item.nominationKw),
                symbol: 'rect',
                symbolSize: [60, 4],
                emphasis: {
                    scale: false,
                    itemStyle: {
                        color: 'rgba(187, 195, 195, 1)',
                    },
                    lineStyle: {
                        width: 0,
                    },
                    label: {
                        show: false,
                    },
                },
                lineStyle: {
                    width: 0,
                },
                itemStyle: {
                    color: 'rgba(187, 195, 195, 1)',
                },
                silent: true,
            },
            {
                name: 'Performance bar',
                type: 'bar',
                data: data.map(item => item.performanceKw),
                barWidth: 60,
                z: 2,
                itemStyle: {
                    color: PRIMARY_COLOR,
                },
            },
            {
                name: 'Performance',
                type: 'line',
                data: data.map(item => item.performanceKw),
                symbol: 'rect',
                symbolSize: [60, 4],
                emphasis: {
                    scale: false,
                },
                lineStyle: {
                    width: 0,
                },
                itemStyle: {
                    color: '#238c8b',
                },
            },
            {
                name: 'Performance %',
                type: 'line',
                data: data.map(item => Math.max(item.performanceKw || 0, item.nominationKw || 0)),
                symbolSize: 0,
                emphasis: {
                    scale: false,
                },
                lineStyle: {
                    width: 0,
                },
                label: {
                    show: true,
                    position: 'top',
                    formatter: (params: any) => {
                        const item = data[params.dataIndex];
                        if (!item.nominationKw || !isNumber(item.performanceKw)) return 'N/A';
                        return `${new Intl.NumberFormat('en-EN').format(Math.round((item.performanceKw / item.nominationKw) * 100))}%`;
                    },
                    color: '#333',
                    backgroundColor: '#fff',
                    fontSize: 14,
                },
            },
        ],
    };

    return <ReactEChartsCore echarts={echarts} option={option} style={{ height: '100%', width: '100%' }} notMerge lazyUpdate />;
};
